import {
  Box,
  Button,
  Flex,
  Heading,
  Stack,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect } from "react";
import { MdContentCopy } from "react-icons/md";
import { brandPalette } from "../../../../constants/brandPalette";
import MainModel from "../../../../components/Models/main-model";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
} from "../../../../components/Models/admin-modal-theme";

const AUTO_CLOSE_MS = 60_000;

const ResendPasswordModal = ({ data, isOpen, onClose }) => {
  const toast = useToast();

  useEffect(() => {
    if (!isOpen) return undefined;
    const timer = setTimeout(onClose, AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
    // Restart only when a new credentials payload is shown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, data]);

  const phone = data?.phone ?? "";
  const password = data?.unhashed_password ?? "";
  const code = data?.user_name ?? "";

  const clipboardText = [
    `Phone: ${phone || "—"}`,
    `Password: ${password || "—"}`,
    `Code: ${code || "—"}`,
  ].join("\n");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(clipboardText);
      toast({
        title: "Copied to clipboard",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch {
      toast({
        title: "Could not copy",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const header = (
    <Box>
      <Heading
        fontSize="28px"
        fontWeight="800"
        color="var(--text-primary)"
        fontFamily="'Amiri','Tajawal',sans-serif"
        textAlign="center"
      >
        New password
      </Heading>
      <Text
        fontSize="xs"
        mt={1}
        color="var(--text-muted)"
        textTransform="uppercase"
        letterSpacing="0.12em"
        fontWeight="700"
        textAlign="center"
      >
        Closes automatically in 60 seconds
      </Text>
    </Box>
  );

  const fieldBlock = (label, value, { mono = false } = {}) => (
    <Box>
      <Text
        as="span"
        display="block"
        fontSize="14px"
        fontWeight="700"
        color="var(--text-primary)"
        mb={2}
        textAlign="left"
      >
        {label}
      </Text>
      <Box
        px={4}
        py={3}
        bg="var(--surface-muted)"
        border="1px solid"
        borderColor="var(--border-subtle)"
        borderRadius="lg"
        fontSize="md"
        fontWeight="600"
        color="var(--text-primary)"
        fontFamily={mono ? "ui-monospace, monospace" : undefined}
        wordBreak="break-word"
      >
        {value != null && String(value).trim() !== "" ? value : "—"}
      </Box>
    </Box>
  );

  return (
    <MainModel
      header={header}
      closeFunc={onClose}
      openVar={isOpen}
      buttonTitle={null}
      size="md"
      contentProps={adminModalContentProps}
      headerProps={adminModalHeaderProps}
      bodyProps={{ ...adminModalBodyProps, pb: 6 }}
      hideDefaultFooter
    >
      <Stack spacing={8} dir="ltr" w="100%">
        <VStack align="stretch" spacing={5}>
          {fieldBlock("Phone", phone)}
          {fieldBlock("Password", password, { mono: true })}
          {fieldBlock("Code", code, { mono: true })}
        </VStack>
        <Flex justify="flex-end" gap={3} pt={2}>
          <Button
            leftIcon={<MdContentCopy />}
            onClick={handleCopy}
            bg={brandPalette.primary}
            color="white"
            borderRadius="lg"
            fontWeight="800"
            _hover={{ bg: brandPalette.accentDark }}
          >
            Copy to clipboard
          </Button>
          <Button
            onClick={onClose}
            variant="ghost"
            color="var(--text-primary)"
            fontWeight="800"
            _hover={{ textDecoration: "underline", bg: "rgba(91, 13, 22, 0.08)" }}
          >
            Close
          </Button>
        </Flex>
      </Stack>
    </MainModel>
  );
};

export default ResendPasswordModal;
